import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import { BUSINESS_TOOL } from 'constants/paths';
import { useDeleteBusinessPlan } from 'services/businessPlan/hooks';
import { useDuplicateBusinessPlan } from 'services/businessPlan/hooks';
import { useGetLinkPublicStatus } from 'services/businessPlan/hooks/useGetLinkPublicStatus';
import { useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';

import { checkIsOnePageBP } from 'pages/BusinessToolPage/utils';

import { useManageDocumentShare } from 'hooks/useManageDocumentShare';

import { IBusinessPlanEntity } from 'interfaces/businessPlan';

import { ConfirmModal } from 'components/_basic/ConfirmModal/ConfirmModal';
import ModalContext from 'components/ModalManager/modal-context';

import { insertId } from 'helpers/path';
import ToastService from 'helpers/ToastService';

const useBusinessPlanActionMenuHandlers = ({ title, uuid, type }: IBusinessPlanEntity) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isOnePage = checkIsOnePageBP(type);

  const { data: linkStatus } = useGetLinkPublicStatus({ id: uuid, enabled: isOnePage });

  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);

  const deleteBusinessPlanMutation = useDeleteBusinessPlan();

  const duplicateBusinessPlanMutation = useDuplicateBusinessPlan();

  const { sharePDFLinkModal, manageOnePageShare } = useManageDocumentShare({ uuid, linkStatus });

  const copyBusinessPlanHandler = useCallback(async () => {
    try {
      await duplicateBusinessPlanMutation.mutateAsync(uuid);
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  }, []);

  const handleNavigate = () => {
    setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
    navigate(insertId(BUSINESS_TOOL, uuid));
  };

  const { showModal, hideModal } = useContext(ModalContext);

  const confirmDeleteHandler = useCallback(async () => {
    try {
      await deleteBusinessPlanMutation.mutateAsync(uuid);
      localStorage.removeItem(`businessPlanPreviousUpdatedInfo:${uuid}`);
      ToastService.showSuccess(
        t('projectSuccessfullyDeleted', {
          businessPlanTitle: title,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  }, []);

  const handleModalOpen = useCallback(() => {
    showModal(ConfirmModal, {
      title: t('deleteProject'),
      body: t('deleteProjectConfirmText', { businessPlanTitle: title }),
      onAbort: hideModal,
      onConfirm: confirmDeleteHandler,
    });
  }, []);

  const handleRenameClick = (toggleCardTitleField: VoidFunction) => {
    toggleCardTitleField();
  };

  const shareHandler = isOnePage ? manageOnePageShare : sharePDFLinkModal;

  const actionHandlers = useMemo(
    () => ({
      EDIT: handleNavigate,
      COPY: copyBusinessPlanHandler,
      RENAME: handleRenameClick,
      SHARE: shareHandler,
      DELETE: handleModalOpen,
    }),
    [title, uuid, type],
  );

  return actionHandlers;
};

export default useBusinessPlanActionMenuHandlers;
